//
//
// style and item utility functions
//
//
import { keyValueObject } from './utils';
import utils from './utils';
import UserType from '../constants/user';
import { FEEDBACK_TRIGGER_TYPE } from 'component/style_item/type';
import { ITEM_POSITION, ReactionType } from '../constants';
import { PRODUCT_TYPE, TRACK_EVENT, trackEvent } from './analytics';
import constant from './constant';
import { Options } from 'component/MultiSelectPopup/types';
import { Collection } from '../containers/user_reactions/types';
import positionMapping from '../common/json/item-position-mapping.json';

export interface IUserDetail {
    id?: string;
    gender?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    userType?: UserType;
}

interface IProductDetail {
    productId?: string;
    productTaffiId?: string;
    productType?: PRODUCT_TYPE;
    collectionName?: string;
    label?: string;
    value?: string;
    stylistEmail?: string;
    stylistId?: string;
}

interface ICustomAnalyticsData {
    [index: string]: string;
}

interface IFeedbackOpenEvent {
    userDetail: IUserDetail;
    productDetail: IProductDetail;
    type: FEEDBACK_TRIGGER_TYPE;
    customAnalyticsData: ICustomAnalyticsData;
}

interface IAuthorize {
    isAuthorize: boolean;
    navigateEffect: () => void;
}

interface IHandleFeedBack {
    authorize: IAuthorize;
    type: FEEDBACK_TRIGGER_TYPE;
    collectionName?: string;
    isCustomer: boolean;
    getFeedbackOptions: (variables) => void;
    openFeedBack: () => void;
    userDetail: IUserDetail;
    productDetail: IProductDetail;
    customAnalyticsData: ICustomAnalyticsData;
}

interface ISubmitFeedback {
    selectedFeedbacks: Options;
    handleCustomValueError: (state) => void;
    createFeedback: (variables) => void;
    productDetail: IProductDetail;
}

interface IReaction {
    like: boolean;
    dislike: boolean;
    totalLike: number;
    totalDislike: number;
}

interface IHandleReaction {
    authorize: IAuthorize;
    customAuthorize?: () => void;
    productDetail: IProductDetail;
    action: ReactionType;
    reaction: IReaction;
    feedbackType: FEEDBACK_TRIGGER_TYPE;
    setReaction: (state) => void;
    toggleReactionMutation: any;
    isCustomer: boolean;
    getFeedbackOptions: (variable) => void;
    openFeedBack: () => void;
    userDetail: IUserDetail;
    customAnalyticsData: ICustomAnalyticsData;
}

interface IFitObjectPositionInfo {
    widthScale: number;
    heightScale: number;
    scaleX: number;
    scaleY: number;
    scaledWidth: number;
    finalScale: number;
    scaledHeight: number;
    verticalPositionStyle: {
        top?: number | string;
        bottom?: number | string;
    };
    horizontalPositionStyle: {
        left?: number | string;
        right?: number | string;
    };
    translateX: number | string;
    translateY: number | string;
}

const isStyle = (obj: keyValueObject): boolean => {
    return obj && obj.collection === 'styles';
};

//
// ToDo: move it to backend
//
// ToDo: check if the currency is same for all the items
//
const getPrice = (obj: keyValueObject): Number => {
    // ToDo: make it consistent in the backend
    // items returned from algolia: style: [item: data: price]
    // items returned from firestore: style: [item: price]
    if ('price' in obj) {
        return Number(obj.price);
    }
    if (isStyle(obj)) {
        console.debug('getPrice=', obj);
        let items = utils.objToList(obj.items, 'data');
        if (!items.length) {
            items = obj.items;
        }
        return utils.objToList(items, 'price').reduce((partialSum, a) => partialSum + a, 0);
    }
    // console.error('getPrice: invalid price')
    return 0;
};

//
// ToDo: redo all this once backend becomes consistent
//
// bring them to a standard form
const getInfo = (item: keyValueObject, debug: string = 'default') => {
    const input = {
        ...item,
        ...(item?.data && item?.data),
        ...(item?.variant && item?.variant),
    };
    //console.debug("getInfo.input=", debug, input);
    const output = {
        title: input?.title,
        description: input?.description,
        // colors: input?.colors?.toString() || input?.color?.toString(),
        // sizes: input?.sizes?.toString() || input?.size?.toString(),
        // tags: input?.tags?.toString(),
        // categories: input?.categories?.toString(),
        tags: utils.objToList(input?.tags || [], 'name'),
        categories: utils.objToList(input?.categories || [], 'name'),
        purposes: utils.objToList(input?.purposes || [], 'title'),
        ages: utils.objToList(input?.ages || [], 'title'),
        bodyShapes: utils.objToList(input?.bodyShapes || [], 'title'),
        bodyTypes: utils.objToList(input?.bodyTypes || [], 'title'),
        seasons: utils.objToList(input?.seasons || [], 'title'),
        colors: utils.objToList(input?.colors || [], 'title'),
        sizes: utils.objToList(input?.sizes || [], 'title'),
        taffiId: input?.taffiId,
        // purpose: input?.purpose?.toString(),
        // age: input?.age?.toString(),
        // season: input?.season?.toString(),
        price: getPrice(input), //input?.price,
        currency: input?.currency,
        collection: input?.collection,
        id: input?.id,
        creator: input?.creator,
        brand: input?.brand,
        accessLevel: input?.accessLevel,
    };
    //console.debug("getInfo.output=", output);
    return output;
};

const mapItemData = (item) => {
    const mappedData = {
        ...item,
        color: item.data?.colors[0]?.value || '',
        size: item.data?.sizes[0]?.value || '',
        quantity: 1,
    };
    console.debug('mapItemData=', item, mappedData);
    return mappedData;
};

const mapItemDataWithPrimaryImages = (items: any[], style?: any) => {
    let imageJson: any;
    if (style && style.imageJson) {
        imageJson = style.imageJson;
    } else {
        const styleData = items.find((i: any) => i?.data?.collection === 'styles' || i?.collection === 'styles');
        imageJson = styleData?.data?.imageJson;
    }
    return items.reduce((d: any[], i: any) => {
        if (i?.data?.collection === 'items') {
            const itemJson = imageJson?.find((json: any) => json.itemId === i.id);
            const itemData = {
                ...i.data,
                images: itemJson
                    ? styleItemUtils.moveElementToFront(i?.data?.images, itemJson.imageCanvas)
                    : i?.data?.images,
            };
            d.push({
                ...i,
                data: itemData,
                color: i?.data?.colors?.[0]?.value || '',
                size: i?.data?.sizes?.[0]?.value || '',
                quantity: 1,
            });
        }
        return d;
    }, []);
};

const isEditableItem = (item, userDetail) => {
    return (
        item &&
        (userDetail.id === item?.creator ||
            (item?.storeId &&
                userDetail.userType === UserType.Stylist &&
                (userDetail.stores || []).map((s) => s?.id).includes(item?.storeId)))
    );
};

const getFeedbackOpenEventValues = (
    userDetail: IUserDetail,
    productDetail: IProductDetail,
    type: FEEDBACK_TRIGGER_TYPE,
    customAnalyticsData: ICustomAnalyticsData = {},
) => {
    return {
        userInfo: {
            userId: userDetail?.id,
            gender: userDetail?.gender,
            email: userDetail?.email,
            name: `${userDetail?.firstName} ${userDetail?.lastName}`,
            userType: userDetail?.userType,
        },
        targetInfo: {
            type: productDetail?.collectionName,
            ID: productDetail?.productId,
            taffiID: productDetail?.productTaffiId,
        },
        stylistInfo: {
            stylistId: productDetail?.stylistId,
            stylistEmail: productDetail?.stylistEmail,
        },
        trigger: type,
        ...customAnalyticsData,
    };
};

const updateReactionFromResp = (data, updateReactionState) => {
    if (data?.reactionState) {
        const { isAlreadyLike, isAlreadyDislike } = data.reactionState;
        const { totalLike, totalDislike } = data.reactionNumber;
        updateReactionState({
            like: isAlreadyLike,
            dislike: isAlreadyDislike,
            totalLike,
            totalDislike,
        });
    }
};

const handleOpenFeedbackEvent = ({ userDetail, productDetail, type, customAnalyticsData = {} }: IFeedbackOpenEvent) => {
    trackEvent(
        TRACK_EVENT.FEEDBACK_OPEN,
        getFeedbackOpenEventValues(userDetail, productDetail, type, customAnalyticsData),
    );
};

const handleOpenFeedback = ({
    authorize,
    type,
    collectionName,
    isCustomer,
    getFeedbackOptions,
    openFeedBack,
    userDetail,
    productDetail,
    customAnalyticsData = {},
}: IHandleFeedBack) => {
    if (!authorize.isAuthorize) {
        authorize.navigateEffect();
        return;
    }
    getFeedbackOptions({
        variables: {
            input: {
                targetType: [collectionName],
            },
        },
    });
    if (isCustomer) openFeedBack();
    handleOpenFeedbackEvent({
        userDetail,
        productDetail,
        type,
        customAnalyticsData,
    });
};

const handleSubmitFeedback = async ({
    selectedFeedbacks,
    handleCustomValueError,
    createFeedback,
    productDetail,
}: ISubmitFeedback) => {
    const hasCustomFeedback =
        selectedFeedbacks?.options?.find((op) => op?.value === constant.CUSTOM_OPTIONS.OTHER) !== undefined;
    if (!selectedFeedbacks?.customValue?.length && hasCustomFeedback) {
        handleCustomValueError(true);
    } else {
        handleCustomValueError(false);
        if (!selectedFeedbacks?.options?.length) return;
        await createFeedback({
            variables: {
                input: {
                    collectionName: productDetail?.collectionName,
                    docId: productDetail?.productId,
                    contextType: PRODUCT_TYPE.STYLE,
                    contextId: productDetail?.productId,
                    contextTaffiId: productDetail?.productTaffiId,
                    feedbackReasonIds: selectedFeedbacks?.options?.map((op) => op?.id),
                    feedbackSubReason: hasCustomFeedback ? selectedFeedbacks?.customValue : '',
                },
            },
        });
    }
};

const updateReaction = (action: ReactionType, reaction: IReaction, setReaction: (state) => void) => {
    if (action === ReactionType.Like) {
        setReaction({ ...reaction, like: !reaction.like, dislike: false });
    } else {
        setReaction({ ...reaction, like: false, dislike: !reaction.dislike });
    }
};

const handleReaction = async ({
    authorize,
    customAuthorize,
    userDetail,
    productDetail,
    customAnalyticsData,
    action,
    reaction,
    setReaction,
    toggleReactionMutation,
    isCustomer,
    getFeedbackOptions,
    openFeedBack,
    feedbackType,
}: IHandleReaction) => {
    const isLocalUser = utils.isLocalUser();

    if (!authorize.isAuthorize && !isLocalUser) {
        if (customAuthorize) customAuthorize();
        else authorize.navigateEffect();
        return;
    }
    const variables = {
        input: {
            collectionName: productDetail.collectionName,
            type: action,
            contextType: productDetail.productType,
            docId: productDetail?.productId,
            contextId: productDetail?.productId,
            contextTaffiId: productDetail?.productTaffiId,
        },
    };

    if (variables?.input?.docId) {
        updateReaction(action, reaction, setReaction);

        const response = await toggleReactionMutation({ variables });

        if (response?.data?.toggleReaction?.reactionState) {
            updateReactionFromResp(response?.data?.toggleReaction, setReaction);
            const { isAlreadyDislike } = response?.data?.toggleReaction?.reactionState;

            if (isAlreadyDislike && !isLocalUser) {
                handleOpenFeedback({
                    authorize,
                    type: feedbackType,
                    collectionName: variables?.input?.collectionName,
                    isCustomer,
                    getFeedbackOptions,
                    openFeedBack,
                    userDetail,
                    productDetail,
                    customAnalyticsData,
                });
            }
        }
    }
};

interface ItemElement {
    position: string;
    data: any;
}

const splitAndSortItemByPosition = (
    arr: ItemElement[],
): { left: ItemElement[]; mid: ItemElement[]; right: ItemElement[] } => {
    const left: ItemElement[] = [];
    const mid: ItemElement[] = [];
    const right: ItemElement[] = [];

    // Split elements into left, mid, and right arrays
    arr.forEach((element) => {
        if (element.position.startsWith(ITEM_POSITION.LEFT)) {
            left.push(element);
        } else if (element.position.startsWith(ITEM_POSITION.CENTER)) {
            mid.push(element);
        } else if (element.position.startsWith(ITEM_POSITION.RIGHT)) {
            right.push(element);
        }
    });

    // Sort each subarray by number part of the position
    const sortFunction = (a: ItemElement, b: ItemElement) => {
        const numA = parseInt(a.position.split('-')[1]);
        const numB = parseInt(b.position.split('-')[1]);
        return numA - numB;
    };

    left.sort(sortFunction);
    mid.sort(sortFunction);
    right.sort(sortFunction);

    return { left, mid, right };
};

const moveElementToFront = <T>(arr: string[], value: T): string[] => {
    try {
        let index = -1;
        const valueObject = new URL(value as string);
        const valueWithoutSearchParams = valueObject.origin + valueObject.pathname;
        for (const [key, url] of arr.entries()) {
            const urlObj = new URL(url);
            const urlWithoutSearchParams = urlObj.origin + urlObj.pathname;
            if (urlWithoutSearchParams === valueWithoutSearchParams) {
                index = key;
            }
        }

        if (index !== -1) {
            const newArr = arr.slice();
            const element = newArr.splice(index, 1)[0];
            newArr.unshift(element);
            return newArr;
        } else {
            return [value as string, ...arr];
        }
    } catch (e) {
        console.log('Error in moveElementToFront ', e);
    }
    return arr;
};

const getSelectedImages = (item: any, imageJson: any): string[] => {
    let allItemImages = item.images ?? item.data?.images;
    let imageInUse: string | null | undefined = null;
    if (item.style_image && item.style_image !== '') {
        imageInUse = item.style_image;
    }
    if (imageJson && Array.isArray(imageJson) && imageJson.length) {
        const itemJson = imageJson.find((image) => image.itemId === item.id);
        imageInUse = itemJson?.imageCanvas ?? itemJson?.url;
        if (itemJson?.selectedImages?.length) {
            allItemImages = itemJson?.selectedImages;
        }
    }
    return imageInUse
        ? styleItemUtils.moveElementToFront(allItemImages, imageInUse)
        : item.images ?? item.data?.images ?? [];
};

const mapDynamicLayoutColumn = (items, imageJson: any[] | null | undefined = null) => {
    const gridColumns: any[] = [];
    const itemArray: any[] = [];
    let count = 0;
    for (const item of items) {
        if (!item.collection || item.collection !== Collection.Styles) {
            let position: string = '';
            let category: any = item?.category ?? item?.data?.metadata?.category;
            if (!category) {
                switch (count) {
                case 0:
                    position = 'left-0';
                    break;
                case 1:
                    position = 'center-1';
                    break;
                case 2:
                    position = 'center-2';
                    break;
                case 3:
                    position = 'right-1';
                    break;
                case 4:
                    position = 'right-2';
                    break;
                case 5:
                    position = 'right-3';
                    break;
                case 6:
                    position = 'right-4';
                    break;
                default:
                    position = 'right-5';
                    break;
                }
            } else {
                position = positionMapping[category];
                if (!position) {
                    position = 'right-6';
                }
            }

            let firstImageScale = 1;
            let removedBackgroundImageWidth = 0;
            let allItemImages = item.images ?? item.data?.images;
            let imageInUse: string | null | undefined = null;
            let matchedImage: string | null = null;
            if (item.style_image && item.style_image !== '') {
                imageInUse = item.style_image;
            }
            if (imageJson && Array.isArray(imageJson) && imageJson.length) {
                const itemJson = imageJson.find((image) => image.itemId === item.id);
                imageInUse = itemJson?.imageCanvas ?? itemJson?.url;
                matchedImage = itemJson?.url;
                if (itemJson?.selectedImages?.length) {
                    allItemImages = itemJson?.selectedImages;
                }
                removedBackgroundImageWidth = itemJson?.width;
            }

            count++;
            const itemImages = imageInUse
                ? styleItemUtils.moveElementToFront(allItemImages, imageInUse)
                : item.images ?? item.data?.images ?? [];

            const outputItem: any = {
                position,
                data: {
                    ...(item.data ?? item),
                    images: itemImages,
                },
            };

            outputItem.data.firstImageMetadata = getImageMetadataDetail(
                outputItem.data,
                [],
                matchedImage ?? itemImages[0],
            );

            if (removedBackgroundImageWidth && outputItem.data.firstImageMetadata?.width) {
                firstImageScale = removedBackgroundImageWidth / outputItem.data.firstImageMetadata.width;
            }
            outputItem.data.firstImageScale = firstImageScale;

            itemArray.push(outputItem);
        }
    }
    const { left, mid, right } = styleItemUtils.splitAndSortItemByPosition(itemArray);
    if (left.length) {
        gridColumns.push(left);
    }
    if (mid.length) {
        gridColumns.push(mid);
    }
    if (right.length) {
        gridColumns.push(right);
    }
    return gridColumns;
};

const isInViewport = (element) => {
    try {
        if (element) {
            const rect = element.getBoundingClientRect();
            return (
                rect.top >= 0 &&
                rect.left >= 0 &&
                rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
                rect.right <= (window.innerWidth || document.documentElement.clientWidth)
            );
        }
        return false;
    } catch (e) {
        console.log('Error when checking if element is in viewport ', e);
        return false;
    }
};

const getAdditionalImageGridRowSize = (position) => {
    let addRowSize = 1;
    switch (position) {
    case 'right-0':
    case 'right-2':
    case 'right-4':
    case 'right-5':
        addRowSize = 2;
        break;
    default:
        addRowSize = 1;
    }
    return addRowSize;
};

const mergeItemArrayObjects = (queryItems: any[], algoliaItems: any[]) => {
    const algoliaItemMap: Map<string, any> = new Map();
    for (const i of algoliaItems) {
        algoliaItemMap.set(i.id, i);
    }
    return queryItems.map((item: any) => {
        const mapItem = algoliaItemMap.get(item.id);
        return {
            ...item,
            ...(mapItem ?? {}),
        };
    });
};

const isMatchedCategory = (objectDetectCategories: string[], category: any): boolean => {
    let topLevelCategory = '';
    if (category?.includes('/')) {
        topLevelCategory = category.split('/')[0]?.trim();
    } else if (category?.length) {
        topLevelCategory = category.trim();
    }
    return objectDetectCategories?.some((c: string) => {
        return (c.includes('/*') && c.includes(topLevelCategory)) || c === category;
    });
};

const getImageMetadataDetail = (
    item: any,
    objectDetectCategories: string[],
    image: string,
    isCanvasLayout?: boolean,
    itemJson?: any,
) => {
    if (!item) {
        return null;
    }
    let imageDetail: any = null;
    if (!item) {
        return imageDetail;
    }
    const category: any = item?.metadata?.category;
    const imagesMetadata = item?.image_metadata ?? item?.metadata?.imageMetadata ?? item?.metadata?.detect?.records;
    // check if image exists in backgroundRemovedMap and position is center-0
    // if yes, then use the bounding box from backgroundRemovedMap
    const backgroundRemovedMap = item?.backgroundRemovedMap;
    if (backgroundRemovedMap) {
        const map = backgroundRemovedMap.find((m: any) => {
            try {
                const originalUrl = removeQueryParams(m.originalUrl);
                const imageUrl = removeQueryParams(image);
                return originalUrl && originalUrl === imageUrl;
            } catch (e) {
                return false;
            }
        });
        if (map) {
            const { width, height, xmin, xmax, ymin, ymax } = map;
            if (
                width &&
                height &&
                typeof xmin === 'number' &&
                typeof xmax === 'number' &&
                typeof ymin === 'number' &&
                typeof ymax === 'number'
            ) {
                imageDetail = {
                    width,
                    height,
                    xmin,
                    ymin,
                    xmax,
                    ymax,
                };
                if (itemJson?.isFullWidth) {
                    return imageDetail;
                }
            }
        }
    }
    if (
        (!objectDetectCategories.length || isMatchedCategory(objectDetectCategories, category)) &&
        imagesMetadata?.length
    ) {
        const imageMetadata = imagesMetadata.find((m: any) => {
            try {
                const metadataUrl = new URL(m._url ?? m.url);
                const imageUrl = new URL(image);
                return metadataUrl.origin + metadataUrl.pathname === imageUrl.origin + imageUrl.pathname;
            } catch (e) {
                return false;
            }
        });
        let width: number | null = null;
        let height: number | null = null;
        let bound_boxes: any[] = [];
        let xminArray: any = [],
            xMaxArray: any = [],
            yMinArray: any = [],
            yMaxArray: any = [];

        if (imageMetadata && imageMetadata._objects?.length) {
            const matchedObject = imageMetadata._objects.filter((obj: any) => obj.Category === category);
            const { _width, _height } = imageMetadata;
            width = _width;
            height = _height;
            for (const matched of matchedObject) {
                if (matched.bound_box?.length) {
                    const { bound_box } = matched;
                    bound_boxes.push(bound_box);
                    xminArray.push(bound_box[0]);
                    yMinArray.push(bound_box[1]);
                    xMaxArray.push(bound_box[2]);
                    yMaxArray.push(bound_box[3]);
                }
            }
        } else if (imageMetadata && imageMetadata.bound_box?.length) {
            width = imageMetadata.width;
            height = imageMetadata.height;
            for (const bound_box of imageMetadata.bound_box) {
                bound_boxes.push(bound_box);
                const { xmin, ymin, xmax, ymax } = bound_box;
                xminArray.push(xmin);
                yMinArray.push(ymin);
                xMaxArray.push(xmax);
                yMaxArray.push(ymax);
            }
        }
        if (
            bound_boxes.length &&
            width &&
            height &&
            xminArray.length &&
            xMaxArray.length &&
            yMinArray.length &&
            yMaxArray.length
        ) {
            const boundingXmax = Math.max(...xMaxArray);
            const boundingXmin = Math.min(...xminArray);
            const boundingYmax = Math.max(...yMaxArray);
            const boundingYmin = Math.min(...yMinArray);
            let _boundingXmax = boundingXmax;
            let _boundingXmin = boundingXmin;
            let _boundingYmax = boundingYmax;
            let _boundingYmin = boundingYmin;
            // the below code is commented out because it is only needed for style card with border
            if (
                !isCanvasLayout &&
                !category?.includes('Clothing') &&
                (boundingYmax - boundingYmin) / (boundingXmax - boundingXmin) > 2
            ) {
                _boundingXmax = width;
                _boundingXmin = 0;
                _boundingYmax = height;
                _boundingYmin = 0;
            }

            imageDetail = {
                width,
                height,
                xmin: _boundingXmin,
                xmax: _boundingXmax,
                ymin: _boundingYmin,
                ymax: _boundingYmax,
            };
        }
    }
    return imageDetail;
};

const getFitObjectImagePosition = (
    containerWidth: number,
    containerHeight: number,
    imageWidth: number,
    imageHeight: number,
    boundingXmin: number,
    boundingXmax: number,
    boundingYmin: number,
    boundingYmax: number,
    shouldFitObject: boolean,
    shouldUseImageJsonScale: boolean,
    imageJsonScale: number,
    shouldBeSmaller: boolean,
): IFitObjectPositionInfo => {
    const MOBILE_LAST_COLUMN_SCALE_DOWN = 0.7;
    const objectHeight = boundingYmax - boundingYmin;
    const objectWidth = boundingXmax - boundingXmin;
    // Calculate scaling factors
    const widthScale = containerWidth / (shouldFitObject ? objectWidth : imageWidth);
    const heightScale = containerHeight / (shouldFitObject ? objectHeight : imageHeight);

    // Choose the larger scale to cover the entire container
    const maxScale = Math.max(widthScale, heightScale);
    const minScale = Math.min(widthScale, heightScale);
    let scale = shouldFitObject ? minScale : maxScale;
    let scaleX = 1;
    let scaleY = 1;
    if (shouldUseImageJsonScale && imageJsonScale) {
        scale = imageJsonScale;
        if (objectWidth * scale > containerWidth) {
            scaleX = scale / ((objectWidth * scale) / containerWidth);
        }
        if (objectHeight * scale > containerHeight) {
            scaleY = scale / ((objectHeight * scale) / containerHeight);
        }
        scale = shouldFitObject ? Math.min(scale, scaleX, scaleY, minScale) : scale;
        if (shouldBeSmaller) {
            scale *= MOBILE_LAST_COLUMN_SCALE_DOWN;
        }
    }

    // Calculate new dimensions for the image
    const scaledWidth = imageWidth * scale;
    const scaledHeight = imageHeight * scale;
    const objectScaledHeight = objectHeight * scale;
    const objectScaledWidth = objectWidth * scale;
    const xMinScaled = boundingXmin * scale;
    const xMaxScaled = boundingXmax * scale;
    const yMinScaled = boundingYmin * scale;
    const yMaxScaled = boundingYmax * scale;

    // Check if the object is closer to the top or bottom
    const objectCenterY = (boundingYmin + boundingYmax) / 2;
    const objectCenterX = (boundingXmin + boundingXmax) / 2;
    const isObjectAboveCenter = objectCenterY < imageHeight / 2;
    const isObjectOnLeft = objectCenterX < imageWidth / 2;

    // Determine vertical position based on the object's position
    let verticalPositionStyle: any = isObjectAboveCenter ? { top: 0 } : { bottom: 0 };
    let horizontalPositionStyle: any = isObjectOnLeft ? { left: 0 } : { right: 0 };

    let translateY: number | string = 0;
    if (objectScaledHeight > containerHeight) {
        // put the image at the vertically center
        translateY = isObjectAboveCenter
            ? containerHeight / 2 - objectCenterY * scale
            : objectCenterY * scale - containerHeight / 2;
    } else if (isObjectAboveCenter) {
        // object is in the upper half and part of the object is overflowing from the container
        if (yMaxScaled > containerHeight) {
            translateY = -yMinScaled;
            if (scaledHeight > containerHeight) {
                const space = containerHeight - objectScaledHeight;
                translateY = translateY + space / 2;
            }
        }
    } else if (!isObjectAboveCenter) {
        if (objectScaledHeight + (scaledHeight - yMaxScaled) > containerHeight) {
            // object is in the bottom half and part of the object overflows from the container
            translateY = scaledHeight - yMaxScaled;
            if (scaledHeight > containerHeight) {
                const space = containerHeight - objectScaledHeight;
                translateY = translateY - space / 2;
            }
        }
    }

    let translateX: number | string = 0;
    if (objectScaledWidth > containerWidth) {
        // put the image at the horizontal center
        translateX = isObjectOnLeft
            ? containerWidth / 2 - objectCenterX * scale
            : objectCenterX * scale - containerWidth / 2;
    } else if (isObjectOnLeft) {
        // The object is mostly in the left half and part of the object overflows the container
        if (xMaxScaled > containerWidth) {
            translateX = -xMinScaled;
            if (scaledWidth > containerWidth) {
                // If there is space left from the right edge of the object to the right edge of the container
                const space = containerWidth - objectScaledWidth;
                // adjust translateX to centralize the object
                translateX = translateX + space / 2;
            }
        } else if (scaledWidth > containerWidth) {
            // centralize the object
            translateX = -Math.abs((containerWidth - xMaxScaled - xMinScaled) / 2);
        }
    } else if (!isObjectOnLeft) {
        if (objectScaledWidth + (scaledWidth - xMaxScaled) > containerWidth) {
            // The object is mostly in the right half and part of the object overflows the container
            translateX = scaledWidth - xMaxScaled;
            // If there is space left from the left edge of the object to the left edge of the container
            const space = containerWidth - objectScaledWidth;
            // adjust translateX to centralize the object
            translateX = translateX - space / 2;
        } else if (scaledWidth > containerWidth) {
            // centralize the object
            translateX = Math.abs((containerWidth - xMaxScaled - xMinScaled) / 2);
        }
    }
    if (scaledHeight < containerHeight && scaledWidth < containerWidth) {
        verticalPositionStyle = {
            top: '50%',
        };
        horizontalPositionStyle = {
            left: '50%',
        };
        translateY = '-50%';
        translateX = '-50%';
    }
    if (scaledHeight > containerHeight && scaledWidth < containerWidth) {
        horizontalPositionStyle = {
            left: '50%',
        };
        translateX = '-50%';
    }
    if (scaledWidth > containerWidth && scaledHeight < containerHeight) {
        verticalPositionStyle = {
            top: '50%',
        };
        translateY = '-50%';
    }
    return {
        widthScale,
        heightScale,
        scaleX,
        scaleY,
        finalScale: scale,
        scaledWidth,
        scaledHeight,
        verticalPositionStyle,
        horizontalPositionStyle,
        translateX: typeof translateX === 'number' ? translateX + 'px' : translateX,
        translateY: typeof translateY === 'number' ? translateY + 'px' : translateY,
    };
};
const removeQueryParams = (url: string) => {
    try {
        const urlObj = new URL(url);
        urlObj.search = '';
        return urlObj.toString();
    } catch (error) {
        return null;
    }
};
const verifyItemBackgroundRemovedImage = (imageJson: any, items: any[], stylistId?: string): boolean => {
    if (stylistId && stylistId !== process.env.REACT_APP_SHOPIFY_STYLIST_ID) {
        return true;
    }
    // check if all imageCanvas are present in the items backgroundRemovedMap
    let countScaleXEqualToOne = 0;
    for (const itemJson of imageJson) {
        const item = items.find((i: any) => i.id === itemJson.itemId);
        const backgroundRemovedMap = item?.backgroundRemovedMap ?? item?.data?.backgroundRemovedMap ?? [];
        const matchedRemovedBackgroundImage = backgroundRemovedMap?.some(
            (elem: any) => removeQueryParams(elem.originalUrl) === removeQueryParams(itemJson.url),
        );
        if (itemJson.scaleX === 1) {
            countScaleXEqualToOne++;
        }
        if (!matchedRemovedBackgroundImage || countScaleXEqualToOne === imageJson.length) {
            console.log('verifyItemBackgroundRemovedImage: backgroundRemovedMap not found for ', itemJson);
            // all scaleX are 1 means this is the fake scale in fake imageJson from the Amira screen
            return false;
        }
    }
    return true;
};

const getStyleImageSize = (style: any): { width: number; height: number } => {
    const imageJson = style?.imageJson ?? [];
    let maxX = 0;
    let maxY = 0;

    imageJson.forEach((img: any) => {
        let item = style.items.find((i: any) => i.id === img.itemId);
        if (item?.data) {
            item = item.data;
        }
        const imageDetail = getImageMetadataDetail(item, [], img.url, true, img);
        let finalScale = img.scaleX;
        let additionalScale = 1;
        if (imageDetail?.width && imageDetail?.width !== img.width) {
            additionalScale = img.width / imageDetail.width;
            finalScale = img.scaleX * additionalScale;
        }
        let imageWidth = imageDetail?.xmax ? imageDetail?.xmax : img.width;
        let imageHeight = imageDetail?.ymax ? imageDetail?.ymax : img.height;
        const scaledWidth = imageWidth * finalScale;
        const scaledHeight = imageHeight * finalScale;

        const left = img.left;
        const top = img.top;
        const right = left + scaledWidth;
        const bottom = top + scaledHeight;
        maxX = !maxX ? right : Math.max(maxX, right);
        maxY = !maxY ? bottom : Math.max(maxY, bottom);
    });
    const divWidth = maxX;
    const divHeight = maxY;
    return {
        width: Math.ceil(divWidth),
        height: Math.ceil(divHeight),
    };
};

const styleItemUtils = {
    isStyle,
    getInfo,
    mapItemData,
    isEditableItem,
    getFeedbackOpenEventValues,
    updateReactionFromResp,
    handleOpenFeedbackEvent,
    handleSubmitFeedback,
    handleReaction,
    splitAndSortItemByPosition,
    moveElementToFront,
    mapDynamicLayoutColumn,
    isInViewport,
    getAdditionalImageGridRowSize,
    mergeItemArrayObjects,
    getImageMetadataDetail,
    getFitObjectImagePosition,
    verifyItemBackgroundRemovedImage,
    getStyleImageSize,
    getSelectedImages,
    mapItemDataWithPrimaryImages,
};

export default styleItemUtils;
